import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApi } from '../../../helpers/fetch';
import { AdminDetailPageSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import Icon from '../../components/Icon/Icon';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import {
    BarChart as RechartsBarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { GENRE_LABELS } from '../../constants/genres';
import moment from 'moment';
import { getUserDisplayName } from '../../../helpers/userDisplayName';
import './AdminUserProfile.scss';
import '../Admin/Admin.scss';

const AdminUserProfile = () => {
    const { user_id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetchApi(`/admin/users/${user_id}/profile`);

                if (response.success && response.data) {
                    setUserData(response.data);
                } else {
                    setError('User not found');
                }
            } catch (err) {
                setError('Failed to load user profile');
                console.error('Error fetching user profile:', err);
            } finally {
                setLoading(false);
            }
        };

        if (user_id) {
            fetchUserProfile();
        }
    }, [user_id]);

    const formatCurrency = (amount) => {
        if (typeof amount === 'object' || (!amount && amount !== 0)) return '—';
        const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
        if (isNaN(numAmount)) return '—';
        return `€${(numAmount / 100).toFixed(2)}`;
    };

    const formatTime = (hours) => {
        if (typeof hours === 'object' || (!hours && hours !== 0)) return '—';
        const numHours = typeof hours === 'number' ? hours : parseFloat(hours);
        if (isNaN(numHours)) return '—';
        if (numHours < 24) {
            return `${Math.round(numHours)}h`;
        }
        const days = Math.floor(numHours / 24);
        const hoursRemainder = Math.round(numHours % 24);
        if (days === 1 && hoursRemainder === 0) {
            return `${days}d`;
        }
        if (hoursRemainder > 0) {
            return `${days}d ${hoursRemainder}h`;
        }
        return `${days} day${days > 1 ? 's' : ''}`;
    };

    const formatTimeSpent = (minutes, seconds) => {
        if (typeof minutes === 'object' || (!minutes && minutes !== 0)) return '—';
        const numMinutes = typeof minutes === 'number' ? minutes : parseFloat(minutes);
        if (isNaN(numMinutes)) return '—';
        const secs = (typeof seconds === 'number' ? seconds : parseFloat(seconds)) || 0;
        return `${numMinutes}mins ${secs}secs`;
    };

    // Helper to safely convert any value to a string for rendering
    const safeValue = (value) => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'object') return '—';
        if (typeof value === 'number') return value;
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return String(value);
    };

    if (loading) {
        return (
            <AdminDetailPageSkeleton className="admin-user-profile" />
        );
    }

    if (error || !userData) {
        return (
            <div className="admin-user-profile">
                <div className="error-state">
                    <Icon alias="alert" color="#FF3232" width={64} />
                    <h2>{error || 'User not found'}</h2>
                    <button onClick={() => navigate('/admin/users')} className="back-btn">
                        Back to Users
                    </button>
                </div>
            </div>
        );
    }

    const isCreator = userData.role === 'creator' || (userData.creations_count && userData.creations_count > 0);

    return (
        <div className="admin-user-profile">
            {/* Breadcrumb Header */}
            <div className="profile-header">
                <div className="breadcrumb">
                    <button onClick={() => navigate('/admin/users')} className="breadcrumb-link">
                        Users {userData.total_users_count ? `[${userData.total_users_count}]` : ''}
                    </button>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">Profile</span>
                </div>
                <button className="view-profile-btn" onClick={() => window.open(`/user-profile?user_id=${user_id}`, '_blank')}>
                    VIEW PROFILE
                </button>
            </div>

            {/* User Profile Information with Activity Metrics */}
            <div className="profile-info-section">
                <div className="profile-left">
                    <div className='d-flex f-row jcsb aic'>
                        <div className="profile-avatar-name">
                            <div className="profile-name-section">
                                <h1 className="profile-name">{getUserDisplayName(userData)}</h1>
                                <p className="membership-date">
                                    Member since {moment(userData.created_at).format('DD/MM/YYYY')}
                                </p>
                                <div className="profile-badges">
                                    <button
                                        className={`role-toggle creator selected`}
                                    >
                                        {isCreator ? 'Creator' : 'User'}
                                    </button>
                                    {userData.accountStatus?.toLowerCase() === 'active' && (
                                        <button
                                            className={`status-toggle active selected`}
                                            onClick={() => userData.accountStatus?.toLowerCase() !== 'active' && handleStatusToggle(userData, 'active')}
                                        >
                                            Active
                                        </button>)}
                                </div>
                            </div>
                        </div>
                        <div className="profile-details">
                            <div className="detail-item">
                                <span className="detail-label">Email address:</span>
                                <span className="detail-value">{userData.email}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Full name:</span>
                                <span className="detail-value">{getUserDisplayName(userData)}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Sign up:</span>
                                <span className="detail-value">
                                    {userData.inviter_username
                                        ? `Invited by @${userData.inviter_username.replace('@', '')}`
                                        : userData.invited_by_user_id
                                            ? `User #${userData.invited_by_user_id}`
                                            : 'Direct signup'}
                                </span>
                            </div>
                            {userData.last_login && (
                                <div className="detail-item">
                                    <span className="detail-label">Last Login:</span>
                                    <span className="detail-value">{moment(userData.last_login).format('DD/MM/YYYY HH:mm')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='profile-info-left'>
                        <div className='item-left'
                        >
                            <Icon alias="heart" width={56} height={56} stroke="#FF8CDC" strokeWidth={3} color='#FFF' />
                            <span className='label-count'>{safeValue(userData.likes_count) || 0}</span>
                            <span className='label-name'>Likes</span>

                        </div>
                        <div className='item-left'>
                            <Icon alias="users-profile" width={56} height={56} stroke="#FF8CDC" strokeWidth={3} color='#FFF' />
                            <span className='label-count'>{safeValue(userData.subscribers) || 0}</span>
                            <span className='label-name'>Subscriptions</span>
                        </div>
                        <div className='item-left'>
                            <Icon alias="contribution-profile" width={56} height={56} stroke="#FF8CDC" strokeWidth={1} color='none' />
                            <span className='label-count'>{safeValue(userData.total_spent) || 0}</span>
                            <span className='label-name'>Spent</span>
                        </div>
                        <div className='item-left'>
                            <Icon alias="library-profile" width={56} height={56} stroke="#FF8CDC" strokeWidth={3} color='#FFF' />
                            <span className='label-count'>{safeValue(userData.library_count) || 0}</span>
                            <span className='label-name'>In Library</span>
                        </div>

                    </div>
                </div>
                <div className="profile-right">
                    <div className="activity-metrics-list">
                        <KPICard
                            title="Total time spent:"
                            value={formatTimeSpent(userData.total_time_spent_minutes, userData.total_time_spent_seconds)}
                            loading={loading}
                        />
                        <KPICard title="Avg. daily time spent:" value={formatTimeSpent(userData.avg_daily_time_spent_minutes, userData.avg_daily_time_spent_seconds)} loading={loading} />
                        <KPICard title="Total purchases:" value={safeValue(userData.total_purchases) || 0} loading={loading} />
                        <KPICard title="Total one-time contributions:" value={safeValue(userData.total_one_time_contributions) || 0} loading={loading} />
                        <KPICard title="Total recurring contributions:" value={safeValue(userData.total_recurring_contributions) || 0} loading={loading} />
                        <KPICard title="Current recurring contributions:" value={safeValue(userData.current_recurring_contributions) || 0} loading={loading} />
                        <KPICard title="Pending orders:" value={safeValue(userData.pending_orders) || 0} loading={loading} />
                        <KPICard title="Abandoned carts:" value={safeValue(userData.abandoned_carts) || 0} loading={loading} />
                    </div>
                </div>
            </div>
            
            {/* Time-to-Action Metrics */}
            <div className="time-to-action-section">
                <div className="time-kpis-grid">
                    <KPICard
                        title="Time to first Contribute (rec.)"
                        value={formatTime(userData.time_to_first_contribute_recurring)}
                        loading={loading}
                    />
                    <KPICard
                        title="Time to first Contribute (once)"
                        value={formatTime(userData.time_to_first_contribute_once)}
                        loading={loading}
                    />
                    <KPICard
                        title="Time to first Physical Purchase"
                        value={formatTime(userData.time_to_first_physical_purchase)}
                        loading={loading}
                    />
                    <KPICard
                        title="Time to first Digital Purchase"
                        value={formatTime(userData.time_to_first_digital_purchase)}
                        loading={loading}
                    />
                </div>
            </div>
            <div className='profile-info-section'>

{/* Social Media Icons Block */}
<div className="social-icons-block">
    <div className="social-icons-container">
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/instagram.svg" alt="Instagram" className="social-icon" />
        </a>
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/linkedin.svg" alt="LinkedIn" className="social-icon" />
        </a>
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/youtube.svg" alt="YouTube" className="social-icon" />
        </a>
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/tiktok.svg" alt="TikTok" className="social-icon" />
        </a>
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/x.svg" alt="X" className="social-icon" />
        </a>
        <a href="#" className="social-icon-link" onClick={(e) => { e.preventDefault(); /* TODO: Add endpoint */ }}>
            <img src="/social-icons/facebook.svg" alt="Facebook" className="social-icon" />
        </a>
    </div>
</div>
<div className='activity-metrics-list'>
    <KPICard 
        title="Creator tier (following)" 
        loading={loading} 
        value={typeof userData.creator_tier_following === 'object' ? '—' : (userData.creator_tier_following || '—')} 
    />
    <KPICard 
        title="Potential reach" 
        loading={loading} 
        value={
            typeof userData.potential_reach === 'object' 
                ? '—' 
                : (userData.potential_reach !== undefined 
                    ? (typeof userData.potential_reach === 'number' 
                        ? userData.potential_reach.toLocaleString() 
                        : String(userData.potential_reach || 0))
                    : '—')
        } 
    />
</div>
</div>
          

            {/* Creator Analytics Section */}
            {true && (
                <>
                    <div className="creator-analytics-section">
                        <div className="creator-info-left">
                            <h3 className="section-title">Creator Analytics</h3>
                            <div className="creator-details">
                                <KPICard 
                                    title="First post date" 
                                    loading={loading} 
                                    value={userData.first_post_date 
                                        ? moment(userData.first_post_date).format('DD MMM YYYY')
                                        : '—'} 
                                />
                                <KPICard 
                                    title="Time to first earning" 
                                    loading={loading} 
                                    value={userData.time_to_first_earning !== undefined
                                        ? (userData.time_to_first_earning_days
                                            ? `${userData.time_to_first_earning_days} days`
                                            : formatTime(userData.time_to_first_earning))
                                        : '—'} 
                                />
                                <KPICard 
                                    title="Avg. Monthly Revenue" 
                                    loading={loading} 
                                    value={userData.avg_monthly_revenue !== undefined
                                        ? formatCurrency(userData.avg_monthly_revenue)
                                        : '—'} 
                                />
                                <KPICard 
                                    title="Creator tier (Earnings)" 
                                    loading={loading} 
                                    value={safeValue(userData.creator_tier_earnings) || '—'} 
                                />
                                <KPICard 
                                    title="Creator tier (following)" 
                                    loading={loading} 
                                    value={safeValue(userData.creator_tier_following) || '—'} 
                                />
                                <KPICard 
                                    title="Potential reach" 
                                    loading={loading} 
                                    value={typeof userData.potential_reach === 'number'
                                        ? userData.potential_reach.toLocaleString()
                                        : safeValue(userData.potential_reach) || '—'} 
                                />
                            </div>
                        </div>

                        {/* Key Creator Metrics */}
                        <div className="key-creator-metrics">
                            <div className="key-metric">
                                <div className="key-metric-icon">€</div>
                                <div className="key-metric-content">
                                    <span className="key-metric-value">{formatCurrency(userData.earnings)}</span>
                                    <span className="key-metric-label">Earned</span>
                                </div>
                            </div>
                            <div className="key-metric">
                                <Icon alias="heart" width={24} height={24} color="#1976d2" />
                                <div className="key-metric-content">
                                    <span className="key-metric-value">{safeValue(userData.total_likes) || 0}</span>
                                    <span className="key-metric-label">Likes</span>
                                </div>
                            </div>
                            <div className="key-metric">
                                <Icon alias="person" width={24} height={24} color="#1976d2" />
                                <div className="key-metric-content">
                                    <span className="key-metric-value">
                                        {typeof userData.subscribers === 'number' 
                                            ? userData.subscribers.toLocaleString() 
                                            : safeValue(userData.subscribers) || 0}
                                    </span>
                                    <span className="key-metric-label">Subscribers</span>
                                </div>
                            </div>
                            <div className="key-metric">
                                <Icon alias="person" width={24} height={24} color="#1976d2" />
                                <div className="key-metric-content">
                                    <span className="key-metric-value">{safeValue(userData.contributors) || 0}</span>
                                    <span className="key-metric-label">Contributors</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Statistics */}
                    <div className="dashboard-section">
                        <div className="section-header">
                            <h2>Content Statistics</h2>
                        </div>
                        <div className="creations-news-kpis-grid">
                            <KPICard
                                title="Number of News"
                                value={safeValue(userData.news_count) || 0}
                                trend={userData.news_count_percentage}
                                trendLabel="since last week"
                                absoluteChange={userData.news_count_trend}
                                formatAbsoluteChange={formatCurrency}
                                color={userData.news_count_percentage && userData.news_count_percentage >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                            <KPICard
                                title="Number of Creations"
                                value={safeValue(userData.creations_count) || 0}
                                trend={userData.creations_count_percentage}
                                trendLabel="since last week"
                                absoluteChange={userData.creations_count_trend}
                                formatAbsoluteChange={formatCurrency}
                                color={userData.creations_count_percentage && userData.creations_count_percentage >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                            <KPICard
                                title="Paid Creations"
                                value={safeValue(userData.paid_creations_count) || 0}
                                trend={userData.paid_creations_percentage}
                                trendLabel="since last week"
                                absoluteChange={userData.paid_creations_trend}
                                formatAbsoluteChange={formatCurrency}
                                color={userData.paid_creations_percentage && userData.paid_creations_percentage >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                            <KPICard
                                title="Monetised publications"
                                value={safeValue(userData.monetised_publications_count) || 0}
                                trend={userData.monetised_publications_percentage}
                                trendLabel="since last week"
                                absoluteChange={userData.monetised_publications_trend}
                                formatAbsoluteChange={formatCurrency}
                                color={userData.monetised_publications_percentage && userData.monetised_publications_percentage >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        </div>
                    </div>

                    {/* Creations per Category Chart */}
                    <div className="dashboard-section">
                        <div className="section-header">
                            <h2>Creations per category</h2>
                        </div>
                        <div className="chart-total">
                            Total: {formatCurrency(userData.total_creations_earnings || userData.earnings)}
                        </div>
                        {userData.creations_by_category && userData.creations_by_category.length > 0 ? (
                            <div className="category-chart-wrapper">
                                <ResponsiveContainer width="50%" height={300}>
                                    <RechartsBarChart
                                        data={userData.creations_by_category.map(item => {
                                            const key = item.category?.toLowerCase();
                                            return {
                                                name: GENRE_LABELS[key] || item.category || 'Other',
                                                value: item.count || 0,
                                            };
                                        })}
                                        margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="0"
                                            stroke="#F0F0F0"
                                            vertical={false}
                                        />
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 11, fill: '#666', angle: -45, textAnchor: 'end' }}
                                            height={60}
                                            interval={0}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 12, fill: '#999' }}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(255, 140, 220, 0.1)' }}
                                            content={({ active, payload, label }) => {
                                                if (!active || !payload?.length) return null;
                                                return (
                                                    <div className="category-chart-tooltip">
                                                        <p className="tooltip-label">{label}</p>
                                                        <p className="tooltip-value">
                                                            {payload[0].value} creation{payload[0].value !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Bar
                                            dataKey="value"
                                            fill="#FF8CDC"
                                            radius={[4, 4, 0, 0]}
                                            barSize={40}
                                        />
                                    </RechartsBarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="chart-empty" style={{ height: '300px' }}>
                                <p>No category data available</p>
                            </div>
                        )}
                    </div>

                    {/* Creations Table */}
                    {userData.creations && userData.creations.length > 0 && (
                        <div className="dashboard-section">
                            <div className="section-header">
                                <h2>Creations</h2>
                            </div>
                            <table className="creations-table">
                                <thead>
                                    <tr>
                                        <th>Creations</th>
                                        <th>Earnings</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userData.creations.map((creation) => (
                                        <tr key={creation.creation_id}>
                                            <td>{creation.title}</td>
                                            <td>{formatCurrency(creation.earnings || 0)}</td>
                                            <td>
                                                <button
                                                    className="view-btn"
                                                    onClick={() => window.open(`/creation-detail?creation_id=${creation.creation_id}`, '_blank')}
                                                >
                                                    view
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminUserProfile;

